import { Button } from "~/components/ui/button";
import { Icon } from "~/components/ui/icons/icon";
import { Input } from "~/components/ui/input";
import { useInlineEditor } from "~/hooks/useInlineEditor";

interface NumberEditorProps {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  canEdit?: boolean;
  disabled?: boolean;
  label: string;
  display?: (value: number) => string;
}

export function NumberEditor({ value, onChange, canEdit, disabled, label, display }: NumberEditorProps) {
  const {
    editing,
    saving,
    editState,
    containerRef,
    primaryInputRef,
    startEditing,
    save,
    handleKeyDown,
    handleBlur,
    setField,
  } = useInlineEditor<number>({
    value,
    onSave: onChange,
    toEditState: (v) => ({ value: v?.toString() ?? "" }),
    fromEditState: (state) => {
      const n = parseInt(state.value, 10);
      if (!state.value.trim() || isNaN(n) || n <= 0) return null;
      return n;
    },
  });

  if (editing) {
    return (
      <div ref={containerRef} className="flex gap-2 items-center" onBlur={handleBlur}>
        <Input
          ref={primaryInputRef}
          type="number"
          min="0"
          step="1"
          value={editState.value}
          onChange={(e) => setField("value", e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="0"
          disabled={saving}
          className="w-24 h-8 text-sm"
          aria-label={label}
        />
        <Button
          size="icon-sm"
          variant="ghost"
          onMouseDown={(e) => e.preventDefault()}
          onClick={save}
          disabled={saving}
          aria-label="Confirm"
        >
          <Icon i="check" className="size-6 text-primary" />
        </Button>
      </div>
    );
  }

  if (value) {
    const text = display ? display(value) : String(value);
    const displayEl = <span className="text-sm">{text}</span>;

    if (canEdit) {
      return (
        <button
          type="button"
          onClick={startEditing}
          disabled={disabled}
          className="text-foreground hover:text-primary transition-colors disabled:opacity-50"
        >
          {displayEl}
        </button>
      );
    }

    return displayEl;
  }

  if (canEdit) {
    return (
      <button
        type="button"
        onClick={startEditing}
        disabled={disabled}
        className="text-sm text-muted-foreground hover:text-primary transition-colors cursor-pointer"
      >
        + Add {label.toLowerCase()}
      </button>
    );
  }

  return null;
}
